import { type MaybeRefOrGetter, type Ref, onMounted, ref, toValue } from 'vue'

import type { OutletCategorySlug } from '../constants'

const INTRO_PLAYED_KEY_PREFIX = 'outlet-intro-played'

/**
 * Intro splash gate. The intro plays only once per session (browser tab).
 * Uses sessionStorage to track whether the intro has been played.
 * It's shown only after mount so the server never renders the overlay —
 * avoiding any hydration mismatch.
 *
 * @param categorySlug - Optional category slug to track intro per category.
 *                       If not provided, tracks a single global outlet intro.
 * @returns Object with:
 *  - shouldShowIntro: Whether to show the intro
 *  - dismiss: Hides the intro and marks it as played
 *  - reveal: Re-shows the intro (useful for pull-to-reveal gestures)
 */
export function useOutletIntroState(
  categorySlug?: MaybeRefOrGetter<OutletCategorySlug>,
): {
  shouldShowIntro: Ref<boolean>
  dismiss: () => void
  reveal: () => void
} {
  const shouldShowIntro = ref(false)

  const getStorageKey = () => {
    const slug = categorySlug ? toValue(categorySlug) : null

    return slug ? `${INTRO_PLAYED_KEY_PREFIX}-${slug}` : INTRO_PLAYED_KEY_PREFIX
  }

  onMounted(() => {
    const storageKey = getStorageKey()
    const hasPlayedIntro = sessionStorage.getItem(storageKey)
    shouldShowIntro.value = !hasPlayedIntro
  })

  function dismiss() {
    shouldShowIntro.value = false
    const storageKey = getStorageKey()
    sessionStorage.setItem(storageKey, 'true')
  }

  function reveal() {
    shouldShowIntro.value = true
  }

  return { shouldShowIntro, dismiss, reveal }
}
